import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Compass } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { BRAND } from "@/data/translations";
import { LangToggle } from "@/components/LangToggle";

export const NotFound = () => {
    const { t } = useLanguage();

    return (
        <div className="min-h-screen bg-black text-white flex flex-col">
            <header className="border-b border-white/10 bg-black/80 backdrop-blur-xl sticky top-0 z-40">
                <div className="mx-auto max-w-[1100px] px-5 md:px-10 py-5 flex items-center justify-between">
                    <Link to="/" data-testid="notfound-back-link" className="flex items-center gap-3 group">
                        <picture>
                            <source srcSet={BRAND.logoDarkWebp} type="image/webp" />
                            <img
                                src={BRAND.logoDark}
                                alt="Fit & Wellness"
                                className="h-9 md:h-10 w-auto"
                                decoding="async"
                                width="120"
                                height="40"
                            />
                        </picture>
                    </Link>
                    <div className="flex items-center gap-4">
                        <LangToggle testid="notfound-language-toggle" />
                        <Link
                            to="/"
                            className="hidden sm:inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-white/70 hover:text-fw transition-colors"
                        >
                            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2} />
                            {t.notFound.cta}
                        </Link>
                    </div>
                </div>
            </header>

            <main
                data-testid="notfound-section"
                className="relative flex-1 flex items-center overflow-hidden"
            >
                <div className="absolute inset-0 grid-lines opacity-30 pointer-events-none" />
                <div className="absolute inset-0 grain-overlay pointer-events-none" />
                <div className="absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full bg-fw/15 blur-[160px] pointer-events-none" />
                <div className="absolute bottom-0 right-0 w-[520px] h-[520px] rounded-full bg-fw/10 blur-[180px] pointer-events-none" />

                <div className="relative z-10 mx-auto max-w-[1100px] w-full px-5 md:px-10 py-24 md:py-32">
                    <div className="grid grid-cols-12 gap-6 lg:gap-12 items-center">
                        <div className="col-span-12 lg:col-span-7">
                            <div className="flex items-center gap-3 mb-6">
                                <span className="w-10 h-px bg-fw" />
                                <span
                                    data-testid="notfound-overline"
                                    className="font-mono text-[11px] uppercase tracking-[0.3em] text-fw"
                                >
                                    {t.notFound.overline}
                                </span>
                            </div>

                            <h1
                                data-testid="notfound-headline"
                                className="font-heavy uppercase leading-[0.95] text-5xl md:text-6xl lg:text-7xl mb-6"
                            >
                                <span className="block text-white">404</span>
                                <span className="block text-fw text-glow-green">{t.notFound.title}</span>
                            </h1>

                            <p
                                data-testid="notfound-body"
                                className="text-base md:text-lg text-white/70 leading-relaxed max-w-xl mb-10"
                            >
                                {t.notFound.body}
                            </p>

                            <div className="flex flex-col sm:flex-row gap-3">
                                <Link
                                    to="/"
                                    data-testid="notfound-cta-home"
                                    className="group inline-flex items-center justify-between gap-3 px-5 py-4 bg-fw text-black font-display font-semibold text-[12px] uppercase tracking-[0.18em] rounded-sm hover:bg-fw-glow hover:glow-green transition-all duration-300"
                                >
                                    <span className="flex items-center gap-2">
                                        <ArrowLeft className="w-4 h-4" strokeWidth={2} />
                                        {t.notFound.cta}
                                    </span>
                                </Link>
                                <Link
                                    to="/#protocols"
                                    data-testid="notfound-cta-protocols"
                                    className="group inline-flex items-center justify-between gap-3 px-5 py-4 border border-white/30 text-white font-display font-semibold text-[12px] uppercase tracking-[0.18em] rounded-sm hover:border-fw hover:text-fw transition-all duration-300"
                                >
                                    <span className="flex items-center gap-2">
                                        <Compass className="w-4 h-4" strokeWidth={2} />
                                        {t.notFound.secondary}
                                    </span>
                                    <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2} />
                                </Link>
                            </div>
                        </div>

                        <div className="hidden lg:flex col-span-5 items-center justify-center">
                            <div className="relative">
                                <div className="absolute -inset-4 border border-fw/30 rounded-sm pointer-events-none" />
                                <div className="relative px-10 py-14 bg-black/60 border border-white/10 backdrop-blur-sm">
                                    <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-fw block mb-4">
                                        Method 00 / 07
                                    </span>
                                    <span className="font-heavy text-[160px] leading-none text-white/95 tabular-nums">
                                        404
                                    </span>
                                    <span className="mt-4 block font-mono text-[10px] uppercase tracking-[0.24em] text-white/45">
                                        Off-protocol · Realign
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default NotFound;
